import React, {
  useState,
  useMemo,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'

import styled from 'styled-components'
import Joi from 'joi'
import toast from 'react-hot-toast'
import { RiPencilFill } from 'react-icons/ri'
import { FaTrashCan } from 'react-icons/fa6'
import moment from 'moment'
import { v4 as uuidv4 } from 'uuid'

import P from '../../../components/typography/P'
import { Button } from '../../../components/Button'
import { StandardInput, DatepickerInput } from '../../../components/Input'
import { SingleSelect } from '../../../components/Select'
import RadioButtonGroup from '../../../components/RadioButtonGroup'
import EmptyWrapper from '../../../components/EmptyWrapper'
import ExpandableText from '../../../components/ExpandableText'
import RichTextbox from '../../../components/RichTextbox'

import { getFromLocalStorage, parseJSON, capitalize } from '../../../utils/util'

import { setExperiences } from '../../../redux/feature/applicantSlice'

import apiClient from '../../../helpers/apiClient'

const JobExperiences = forwardRef((props, ref) => {
  const dispatch = useDispatch()

  const { experiences } = useSelector((state) => state.applicant)

  const validationSchema = useMemo(() => {
    return Joi.object({
      industry: Joi.object({
        value: Joi.alternatives().try(Joi.string(), Joi.number()).required(),
        label: Joi.string().required(),
      })
        .label('Industry')
        .required()
        .messages({
          'object.base': 'Industry must be a valid selection.',
          'any.required': 'Industry is required.',
        }),
      companyName: Joi.string().label('Company').required(),
      companyLocation: Joi.string().label('Company Location').required(),
      jobTitle: Joi.string().label('Job Title').required(),
      jobDescriptionHtml: Joi.string()
        .label('Job Description')
        .required()
        .custom((value, helpers) => {
          // Remove HTML tags and trim whitespace
          const cleanedValue = value.replace(/<[^>]*>/g, '').trim()
          if (!cleanedValue) {
            return helpers.error('string.empty')
          }
          return value
        }),
      experienceCurrentStatus: Joi.string()
        .label('Employment Status')
        .required(),
      workType: Joi.string().label('Work Type').required(),
      experienceFrom: Joi.date().label('Date From').required().messages({
        'date.base': '"Date From" must be a valid date',
        'any.required': '"Date From" is required',
      }),
      experienceTo: Joi.date()
        .label('Date To')
        .required()
        .custom((value, helpers) => {
          const from = helpers.state.ancestors[0].experienceFrom
          if (!from) return value

          const fromDate = new Date(from)
          const toDate = new Date(value)

          const diffInDays = (toDate - fromDate) / (1000 * 60 * 60 * 24)

          if (diffInDays < 1) {
            return helpers.error('date.minDays')
          }

          return value
        })
        .messages({
          'date.base': '"Date To" must be a valid date',
          'any.required': '"Date To" is required',
          'date.minDays': '"Date To" must be at least 1 day after "Date From"',
        }),
    })
  }, [])

  const formRef = useRef(null)

  const [fieldErrors, setFieldErrors] = useState({})
  const [showForm, setShowForm] = useState(false)

  const [industryList, setIndustryList] = useState([])

  const [id, setId] = useState(uuidv4())
  const [industry, setIndustry] = useState({})
  const [jobTitle, setJobTitle] = useState('')
  const [company, setCompany] = useState('')
  const [companyLocation, setCompanyLocation] = useState('')
  const [jobDescription, setJobDescription] = useState('')
  const [workType, setWorkType] = useState('Permanent Work From Home')
  const [employmentStatus, setEmploymentStatus] = useState('No longer employed')
  const [from, setFrom] = useState(new Date().toISOString().split('T')[0])
  const [to, setTo] = useState(new Date().toISOString().split('T')[0])

  const [isEdit, setIsEdit] = useState(false)

  const applicantCVData = useMemo(
    () => getFromLocalStorage('applicant-cv-data'),
    []
  )

  const resetFields = () => {
    setIndustry(industryList[0])
    setJobTitle('')
    setCompany('')
    setCompanyLocation('')
    setJobDescription('')
    setWorkType('Permanent Work From Home')
    setEmploymentStatus('No longer employed')
    setFrom(new Date().toISOString().split('T')[0])
    setTo(new Date().toISOString().split('T')[0])
  }

  /**
   * @returns {boolean} Whether the form data is valid.
   */
  const handleNext = () => {
    const meta = parseJSON(applicantCVData?.meta, {})

    const updatedMeta = {
      ...meta,
      experienceState: experiences,
    }

    const updatedApplicantCVData = {
      ...applicantCVData,
      meta: JSON.stringify(updatedMeta),
    }

    // save to local storage...
    localStorage.setItem(
      'applicant-cv-data',
      JSON.stringify(updatedApplicantCVData)
    )

    return true
  }

  const handleSelectIndustry = (e) => setIndustry(e)

  const handleShowForm = () => {
    setFieldErrors({})
    setIsEdit(false)
    setShowForm(true)
  }

  const handleCloseForm = () => {
    setShowForm(false)
    resetFields()
  }

  const handleSaveJobExperience = () => {
    const payload = {
      industry,
      jobTitle,
      companyName: company,
      companyLocation,
      jobDescriptionHtml: jobDescription,
      workType,
      experienceCurrentStatus: employmentStatus,
      experienceFrom: from,
      experienceTo: to,
    }

    // Validate form data against schema, collecting all errors
    const result = validationSchema.validate(payload, { abortEarly: false })

    // If there are validation errors, set error messages for fields and notify the user
    if (result.error) {
      const validationErrors = result.error.details.reduce((acc, curr) => {
        acc[curr.context.key] = curr.message.replace(/"/g, '')
        return acc
      }, {})

      setFieldErrors(validationErrors)
      toast.error('Please fix the validation errors to continue.')

      return
    }

    payload.industry = industry.label

    let newJobExperiences = []

    if (isEdit) {
      payload.id = id
      newJobExperiences = experiences.map((e) =>
        String(e.id) === String(id) ? payload : e
      )
    } else {
      payload.id = uuidv4()
      newJobExperiences = [...experiences, payload]
    }

    dispatch(setExperiences(newJobExperiences))

    setShowForm(false)
    setIsEdit(false)
    resetFields()
  }

  const handleEditJobExperience = (data) => {
    setIsEdit(true)

    setId(data?.id)

    const selectedIndustry = industryList?.filter(
      (e) => e.label === data?.industry
    )[0]
    setIndustry(selectedIndustry)

    setJobTitle(data?.jobTitle)
    setCompany(data?.companyName)
    setCompanyLocation(data?.companyLocation)
    setJobDescription(data?.jobDescriptionHtml)
    setWorkType(data?.workType)
    setEmploymentStatus(data?.experienceCurrentStatus)
    setFrom(data?.experienceFrom)
    setTo(data?.experienceTo)

    setShowForm(true)
    setFieldErrors({})
  }

  const handleDeleteJobExperience = (data) => {
    const removedJobExperience = experiences.filter((e) => e.id !== data.id)

    dispatch(setExperiences(removedJobExperience))

    setShowForm(false)
  }

  useEffect(() => {
    apiClient.get(`/industries`).then((response) => {
      const data = response?.data?.result || []
      const formattedIndustries = data.map((industry) => ({
        value: industry.id,
        label: industry.name,
      }))
      setIndustryList(formattedIndustries)
      setIndustry(formattedIndustries[0])
    })

    if (applicantCVData) {
      const meta = parseJSON(applicantCVData?.meta, {})
      dispatch(setExperiences(meta?.experienceState || []))
    }
  }, [])

  useEffect(() => {
    // automatically scroll to the form
    if (showForm && formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [showForm])

  useImperativeHandle(ref, () => ({
    onNext: handleNext,
  }))

  return (
    <StyledJobExperiences>
      <P size="1.5rem" weight="500" mb="0.25rem">
        Job Experiences
      </P>

      <P color="#6F6F6F" mb="1rem">
        List your past roles, responsibilities and key achievements to showcase
        your job description.
      </P>
      <Button handleClick={handleShowForm} width="50%" btnClass="primary">
        Add new job experience
      </Button>
      <Container>
        {experiences?.length > 0 ? (
          experiences
            ?.slice() // prevent mutating original array
            .sort(
              (a, b) =>
                moment(b.experienceTo).valueOf() -
                moment(a.experienceTo).valueOf()
            )
            .map((e, i) => (
              <ListItem key={i}>
                <ListItemHeader>
                  <P weight="500">{e.jobTitle}</P>

                  <ListItemIconsWrapper>
                    <RiPencilFill
                      className="icon"
                      onClick={() => handleEditJobExperience(e)}
                    />
                    <FaTrashCan
                      className="icon"
                      onClick={() => handleDeleteJobExperience(e)}
                    />
                  </ListItemIconsWrapper>
                </ListItemHeader>
                <ListItemBody>
                  <P>
                    {e.companyName} - {e.companyLocation}
                  </P>
                  <P>
                    {e.workType} - {e.experienceCurrentStatus}
                  </P>
                  <StyledJobDescription>
                    <P weight="500">Responsibilities:</P>
                    <ExpandableText>
                      <P
                        dangerouslySetInnerHTML={{
                          __html: e?.jobDescriptionHtml,
                        }}
                      ></P>
                    </ExpandableText>
                  </StyledJobDescription>

                  <P weight="500">Industry</P>
                  <P>{e.industry}</P>

                  <ListItemFooter>
                    <P weight="500">Duration</P>
                    <P>
                      {moment(e.experienceFrom).format('LL')} -{' '}
                      {moment(e.experienceTo).format('LL')}
                    </P>
                    <P weight="500">{capitalize(e.currentStatus)}</P>
                  </ListItemFooter>
                </ListItemBody>
              </ListItem>
            ))
        ) : (
          <EmptyWrapper maxHeight="100%" marginBottom="1rem" marginTop="1rem">
            You have no job experiences.
          </EmptyWrapper>
        )}

        {showForm && (
          <FormContainer ref={formRef}>
            <FormGroup>
              <StandardInput
                title="Job Title"
                type="text"
                value={jobTitle}
                onChange={(value) => setJobTitle(value)}
                required
                wrapperClass={fieldErrors.jobTitle && 'error'}
                subText={fieldErrors.jobTitle}
                placeholder="Enter Job Title"
                marginBottom="0.5rem"
              />
              <SingleSelect
                title="Industry"
                options={industryList}
                selected={industry}
                placeholder="Select Industry"
                onChange={handleSelectIndustry}
                wrapperClass={fieldErrors.industry && 'error'}
                subText={fieldErrors.industry}
              />
            </FormGroup>
            <FormGroup>
              <StandardInput
                title="Company"
                type="text"
                value={company}
                onChange={(value) => setCompany(value)}
                required
                wrapperClass={fieldErrors.company && 'error'}
                subText={fieldErrors.company}
                placeholder="Enter Company"
                marginBottom="0.5rem"
              />
              <StandardInput
                title="Company Location"
                type="text"
                value={companyLocation}
                onChange={(value) => setCompanyLocation(value)}
                required
                wrapperClass={fieldErrors.companyLocation && 'error'}
                subText={fieldErrors.companyLocation}
                placeholder="Enter Company Location"
                marginBottom="0.5rem"
              />
            </FormGroup>

            <FormGroup>
              <RadioButtonGroup
                title="Work Type"
                name="workType"
                list={[
                  {
                    value: 'Permanent Work From Home',
                    label: 'Permanent Work From Home',
                  },
                  {
                    value: 'Temporary Work From Home',
                    label: 'Temporary Work From Home',
                  },
                  {
                    value: 'Office',
                    label: 'Office',
                  },
                ]}
                selected={workType}
                onChange={setWorkType}
              />
            </FormGroup>
            <FormGroup>
              <RadioButtonGroup
                name="employmentStatus"
                title="Employment Status"
                list={[
                  {
                    value: 'No longer employed',
                    label: 'No longer employed',
                  },
                  {
                    value: 'Currently employed',
                    label: 'Currently employed',
                  },
                ]}
                selected={employmentStatus}
                onChange={setEmploymentStatus}
              />
            </FormGroup>
            <FormGroup>
              <RichTextbox
                content={jobDescription}
                setContent={setJobDescription}
                title="Job Description"
                required
                marginBottom="1rem"
                className={'jobDescriptionHtml' in fieldErrors && 'has-error'}
                errorMessage={
                  'jobDescriptionHtml' in fieldErrors &&
                  fieldErrors.jobDescriptionHtml
                }
              />
            </FormGroup>
            <FormGroup>
              <DatepickerInput
                title="Date From"
                value={from}
                onChange={(date) => setFrom(date)}
                dateFormat="MMMM dd, yyyy"
                placeholderText="MM/YYYY"
              />
              <DatepickerInput
                title="Date To"
                value={to}
                onChange={(date) => setTo(date)}
                dateFormat="MMMM dd, yyyy"
                placeholderText="MM/YYYY"
                wrapperClass={fieldErrors.to && 'error'}
                subText={fieldErrors.to}
              />
            </FormGroup>
            <ButtonWrapper>
              <Button handleClick={handleSaveJobExperience} btnClass="success">
                Save Record
              </Button>
              <Button handleClick={handleCloseForm} btnClass="primary">
                Close Form
              </Button>
            </ButtonWrapper>
          </FormContainer>
        )}
      </Container>
    </StyledJobExperiences>
  )
})

const StyledJobExperiences = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 1rem;
`

const FormGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
`

const ListItem = styled.div`
  width: 100%;
  background: rgb(248, 249, 255);
  padding: 0.5rem;
  border-radius: 3px;
  position: relative;
  margin-top: 1rem;
  font-weight: 300;
`

const ListItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
`

const ListItemIconsWrapper = styled.div`
  .icon {
    font-size: 0.875rem;
    color: rgb(174, 179, 201);
    margin-left: 1rem;
    cursor: pointer;
  }

  .icon:hover {
    color: rgba(47, 52, 81, 1);
  }
`

const ListItemBody = styled.div`
  display: flex;
  flex-direction: column;
`

const StyledJobDescription = styled.div`
  display: flex;
  flex-direction: column;
  margin: 1rem 0;
`

const ListItemFooter = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`

const FormContainer = styled.div`
  padding: 0.5rem;
  margin-top: 1rem;
`

const ButtonWrapper = styled.div`
  display: flex;
  gap: 1rem;
`

JobExperiences.displayName = 'JobExperiences'

export default JobExperiences
